// Typed Plate v53 node builders for one-pager generation.
//
// The one-pager initializer used to emit legacy Slate shapes (`paragraph`,
// `tableWithoutHeaders`, `table-cell`, `unorderedList`/`list-item`, `bgColor`)
// that DealSummaryEditorPlate then ran through slateToPlate.ts on load. These
// builders emit the Plate-native shape directly, reproducing exactly what that
// converter produced, so:
//
//   - `bgColor` is written as `backgroundColor` (converter renamed it; emitting
//     it directly is the only thing keeping the yellow `[Data Not Available]`
//     highlights alive — there is no longer a converter to rename it).
//   - tables are `table` (never `tableWithoutHeaders`) and carry only
//     `type` / `children` / `colSizes` — the converter dropped the legacy
//     `key` / `columns` / `rows` / `custom` bookkeeping fields.
//   - cells are `td` / `th`; their inline content is wrapped in a `p`
//     (Plate cells require a block child).
//   - lists are flattened to sibling `p` blocks carrying `listStyleType` +
//     `indent` (Plate v53's indent-based list model), NOT `ul`/`li`.
//   - cell text alignment is intentionally NOT emitted: the converter never
//     copied `textAlign` onto `td`/`th`, so the live one-pager has always
//     rendered cells unaligned. Re-introducing alignment is a deliberate
//     behavior change, out of scope for this byte-identical rewrite.
//
// Wrong node shapes are compile errors here, replacing the old `any`-typed
// literals + the converter's silent `wrapAsParagraph` fallthrough.

export interface TextNode {
    text: string;
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strikethrough?: boolean;
    code?: boolean;
    color?: string;
    backgroundColor?: string;
    fontSize?: string;
    fontFamily?: string;
}

export type Marks = Omit<TextNode, 'text'>;

export interface PBlock {
    type: 'p';
    children: TextNode[];
    listStyleType?: 'disc' | 'decimal';
    indent?: number;
}

export interface H2Block {
    type: 'h2';
    children: TextNode[];
}

export interface TdCell {
    type: 'td';
    children: Block[];
    backgroundColor?: string;
    verticalAlign?: string;
}

export interface ThCell {
    type: 'th';
    children: Block[];
    backgroundColor?: string;
    verticalAlign?: string;
}

export type Cell = TdCell | ThCell;

export interface TrRow {
    type: 'tr';
    children: Cell[];
}

export interface TableBlock {
    type: 'table';
    children: TrRow[];
    colSizes?: number[];
}

export type Block = PBlock | H2Block | TableBlock;

const toInline = (children: TextNode[]): TextNode[] => (children.length ? children : [{ text: '' }]);

const isBlock = (n: unknown): n is Block =>
    !!n && typeof n === 'object' && typeof (n as any).type === 'string' && (n as any).text === undefined;

/** A text leaf with optional marks. Omitted marks are simply absent. */
export const text = (str: string, marks?: Marks): TextNode => ({ text: str, ...(marks ?? {}) });

/** A paragraph block. Empty children collapse to a single empty leaf. */
export const p = (children: TextNode[]): PBlock => ({ type: 'p', children: toInline(children) });

/** An H2 heading block. */
export const h2 = (children: TextNode[]): H2Block => ({ type: 'h2', children: toInline(children) });

/** An empty paragraph used as vertical spacing between sections. */
export const spacer = (): PBlock => ({ type: 'p', children: [{ text: '' }] });

/**
 * A single indent-based list paragraph. Plate v53 models lists as sibling `p`
 * blocks carrying `listStyleType` + `indent`, not `ul`/`li`.
 */
export const bulletItem = (children: TextNode[], indent = 1, listStyleType: 'disc' | 'decimal' = 'disc'): PBlock => ({
    type: 'p',
    listStyleType,
    indent,
    children: toInline(children),
});

/** Map a list of items (each = inline children) to sibling bullet paragraphs. */
export const bullet = (items: TextNode[][], indent = 1, listStyleType: 'disc' | 'decimal' = 'disc'): PBlock[] =>
    items.map((item) => bulletItem(item, indent, listStyleType));

type CellContent = string | TextNode | TextNode[] | Block[];
interface CellOpts {
    backgroundColor?: string;
    verticalAlign?: string;
}

// Resolve cell content to block children, mirroring the converter's
// ensureBlockChildren: inline content (string / text node / text[]) is wrapped
// in a single paragraph; block content (paragraphs already built, e.g. bullets)
// is used as-is. The block/inline split is decided over the WHOLE array, not
// just its first element, so a malformed mixed array can never be silently
// passed through as cell children.
const cellChildren = (content: CellContent): Block[] => {
    if (typeof content === 'string') return [p([text(content)])];
    if (Array.isArray(content)) {
        const arr = content as Array<TextNode | Block>;
        if (arr.length === 0) return [p([])];
        if (arr.every(isBlock)) return arr as Block[];
        return [p(arr as TextNode[])];
    }
    return [p([content])];
};

export const td = (content: CellContent, opts?: CellOpts): TdCell => ({
    type: 'td',
    children: cellChildren(content),
    ...(opts?.backgroundColor && { backgroundColor: opts.backgroundColor }),
    ...(opts?.verticalAlign && { verticalAlign: opts.verticalAlign }),
});

export const th = (content: CellContent, opts?: CellOpts): ThCell => ({
    type: 'th',
    children: cellChildren(content),
    ...(opts?.backgroundColor && { backgroundColor: opts.backgroundColor }),
    ...(opts?.verticalAlign && { verticalAlign: opts.verticalAlign }),
});

export const tr = (cells: Cell[]): TrRow => ({ type: 'tr', children: cells });

interface TableOpts {
    colSizes?: number[];
}

export const table = (rows: TrRow[], opts?: TableOpts): TableBlock => ({
    type: 'table',
    children: rows,
    ...(opts?.colSizes && { colSizes: opts.colSizes }),
});
